/**
 * csvImport.js — Parse a simple CSV export into Bet records.
 *
 * Supported header (first row, exact names, order flexible):
 *   id,book,bookName,sport,league,event,selection,betType,oddsAmerican,
 *   stake,placedAt,commenceTime,settledAt,status,line,cashOutAmount,margin
 *
 * Required columns: book, sport, event, selection, betType, oddsAmerican,
 * stake, placedAt, status. Everything else is optional.
 * - `book` is the sportsbook key (e.g. draftkings); `bookName` is the
 *   display name (defaults to the key).
 * - `id` may be blank; a local id is generated.
 * - `line`, `cashOutAmount`, `margin` are numeric or blank.
 * - Standard CSV quoting is supported ("field, with comma").
 *
 * Returns { bets, errors } — malformed rows are skipped and reported in
 * errors (with their 1-based line number) instead of throwing, so one
 * bad row never blocks an import.
 */

import { createBet, validateBet } from '../engine/model.js';

const REQUIRED = ['book', 'sport', 'event', 'selection', 'betType', 'oddsAmerican', 'stake', 'placedAt', 'status'];

/** Minimal RFC-4180-ish CSV parser (handles quotes and embedded commas/newlines). */
export function parseCsvText(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  const src = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { field += '"'; i += 1; } else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); field = '';
      rows.push(row); row = [];
    } else field += ch;
  }
  if (field.length > 0 || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => !(r.length === 1 && r[0].trim() === ''));
}

function toNumberOrNull(value) {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function betsFromCsv(text) {
  const rows = parseCsvText(text);
  const errors = [];
  const bets = [];
  if (rows.length === 0) return { bets, errors: [{ line: 0, message: 'empty file' }] };

  const header = rows[0].map((h) => h.trim());
  for (const col of REQUIRED) {
    if (!header.includes(col)) {
      return { bets: [], errors: [{ line: 1, message: `missing required column: ${col}` }] };
    }
  }
  const idx = {};
  header.forEach((h, i) => { idx[h] = i; });
  const get = (row, col) => {
    const v = row[idx[col]];
    return v === undefined ? '' : String(v).trim();
  };

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r];
    const lineNo = r + 1;
    if (row.length < header.length) {
      errors.push({ line: lineNo, message: `expected ${header.length} columns, got ${row.length}` });
      continue;
    }
    const odds = toNumberOrNull(get(row, 'oddsAmerican'));
    const stake = toNumberOrNull(get(row, 'stake'));
    if (odds === null || stake === null) {
      errors.push({ line: lineNo, message: 'oddsAmerican and stake must be numeric' });
      continue;
    }
    const bet = createBet({
      id: get(row, 'id') || undefined,
      book: { key: get(row, 'book'), name: get(row, 'bookName') || get(row, 'book') },
      sport: get(row, 'sport'),
      league: get(row, 'league') || get(row, 'sport'),
      event: get(row, 'event'),
      selection: get(row, 'selection'),
      betType: get(row, 'betType'),
      oddsAmerican: odds,
      stake,
      placedAt: get(row, 'placedAt'),
      commenceTime: get(row, 'commenceTime') || get(row, 'placedAt'),
      settledAt: get(row, 'settledAt') || null,
      status: get(row, 'status'),
      line: toNumberOrNull(get(row, 'line')),
      cashOutAmount: toNumberOrNull(get(row, 'cashOutAmount')),
      margin: toNumberOrNull(get(row, 'margin')),
      source: 'csv',
    });
    const problems = validateBet(bet);
    if (problems.length) {
      errors.push({ line: lineNo, message: problems.join('; ') });
      continue;
    }
    bets.push(bet);
  }
  return { bets, errors };
}
